import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import api from '../../../service/apiService';
import './PaymentMethodModal.css';

const stripePromise = loadStripe(
  'pk_test_51SGmEP7sx6VdQ0QBXYn4mrSJdBTFnSHZ8P4ZHYNh6rnsVJPRAJIb1pfbSoHDEaq4yrpoYu7Vd89oeP8cZRGLqDG700meTskM6R'
);

const PaymentForm = ({ onSuccess, onClose, userId }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [notice, setNotice] = useState(null); // { type: 'success'|'error', message }

  useEffect(() => {
    async function fetchMethods() {
      setLoadingMethods(true);
      try {
        const res = await api.getPaymentMethods(userId);
        setPaymentMethods(res.paymentMethods || []);
      } catch (err) {
        setPaymentMethods([]);
      } finally {
        setLoadingMethods(false);
      }
    }
    if (userId) fetchMethods();
  }, [userId]);

  const handleDelete = async (paymentMethodId) => {
    if (!window.confirm('Remove this card? This action cannot be undone.')) return;
    try {
      await api.deletePaymentMethod(paymentMethodId, userId);
      // Refresh list
      const res = await api.getPaymentMethods(userId);
      setPaymentMethods(res.paymentMethods || []);
      // Notify other components that payment methods changed
      try {
        window.dispatchEvent(new CustomEvent('paymentMethodsChanged'));
      } catch (e) {
        /* ignore */
      }
      setNotice({ type: 'success', message: 'Payment method removed.' });
    } catch (err) {
      console.error('Failed to delete payment method:', err);
      setNotice({ type: 'error', message: 'Failed to remove card. Please try again.' });
    }
  };
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { clientSecret } = await api.createSetupIntent(userId);

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else {
        // Persist payment method ID in backend
        const paymentMethodId = result.setupIntent.payment_method;
        await api.addPaymentMethod(userId, paymentMethodId);
        setSuccess('Payment method added successfully!');
        if (onSuccess) onSuccess('Payment method added successfully!');
        // Notify other components that payment methods changed
        try {
          window.dispatchEvent(new CustomEvent('paymentMethodsChanged'));
        } catch (e) {
          /* ignore */
        }
        setNotice({ type: 'success', message: 'Payment method added.' });
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError('Failed to add payment method.');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-hide notices
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  return (
    <>
      <div className="pm-list-section">
        <h3 className="pm-list-title">Your Saved Cards</h3>
        {notice && (
          <div className={`pm-notice ${notice.type === 'error' ? 'error' : 'success'}`}>
            {notice.message}
          </div>
        )}
        {loadingMethods ? (
          <div className="pm-list-loading">Loading...</div>
        ) : paymentMethods.length === 0 ? (
          <div className="pm-list-empty">No saved cards yet.</div>
        ) : (
          <ul className="pm-list">
            {paymentMethods.map((pm) => (
              <li className="pm-list-row" key={pm.id}>
                <span className="pm-list-brand">{pm.brand?.toUpperCase() || 'Card'}</span>
                <span className="pm-list-last4">•••• {pm.last4}</span>
                <span className="pm-list-exp">
                  Exp {pm.exp_month}/{pm.exp_year}
                </span>
                <button
                  className="pm-list-trash"
                  title="Remove card"
                  onClick={() => handleDelete(pm.id)}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c33"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m5 0V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <form className="payment-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="card-element">Card Details</label>
          <CardElement id="card-element" options={{ style: { base: { fontSize: '16px' } } }} />
        </div>
        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" className="save-button" disabled={!stripe || isLoading}>
            Add Payment Method
          </button>
        </div>
      </form>
    </>
  );
};

const PaymentMethodModal = ({ isOpen, onClose, onSuccess, userId }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Add Payment Method</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <Elements stripe={stripePromise}>
            <PaymentForm onSuccess={onSuccess} onClose={onClose} userId={userId} />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;
